import { defineMessages, useIntl } from 'react-intl';
import { ROUTES } from 'src/constants/routes.const';
import { LinkButton } from 'src/components';
import { useBackCta } from 'src/hooks';
import { useExperienceContext } from 'src/context/experience';
import { PathContinueButtons } from 'src/apps/Path/PathContinueButtons';
import soundFile from '../assets/activity_finished.mp3';
import { PronunciationViewWrapper } from '../PronunciationViewWrapper';
import * as S from './styles';

const msgs = defineMessages({
  title: { id: 'pronunciationBuddy_endOfSession_title' },
  description: { id: 'pronunicationBuddy_endOfSession_description' },
  finishCta: { id: 'pronunciationBuddy_endOfSession_finishCTA' },
  restartCta2024: { id: 'pronunciationBuddy_endOfSession_goAgainCTA' },
  restartCta2025: { id: 'learnq_eosCta_pronounceMoreWords' },
  phrasesPracticed: {
    id: 'pronunciationBuddy_endOfSession_phrasesPracticed',
    defaultMessage:
      '{count, plural, one {{count} phrase practiced} other {{count} phrases practiced}}',
  },
});

export const SessionCompletedView = ({
  onRestart,
  pathwayNodeId = null,
  languagePairId = null,
  phraseCount = 0,
}: {
  onRestart: () => void;
  pathwayNodeId?: number | null;
  languagePairId?: number | null;
  phraseCount?: number;
}) => {
  const intl = useIntl();
  const { experienceType } = useExperienceContext();
  const backCta = useBackCta(ROUTES.ai.buddies);

  /* istanbul ignore next: please test in WEBSITE-7219 */
  if (!experienceType) return null;

  return (
    <PronunciationViewWrapper $background="primary98">
      {/* decorative sound SFX, captions not applicable */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio autoPlay preload="auto" src={soundFile} data-testid="session-completed-sound" />
      <S.Layout>
        <S.BuddyIcon name="buddyFull" />
        <S.Title data-testid="SessionCompleted-title">{intl.formatMessage(msgs.title)}</S.Title>
        <S.Description>{intl.formatMessage(msgs.description)}</S.Description>
        {pathwayNodeId !== null && (
          <S.Description data-testid="SessionCompleted-phraseCount">
            {intl.formatMessage(msgs.phrasesPracticed, { count: phraseCount })}
          </S.Description>
        )}
        {pathwayNodeId !== null ? (
          <S.PathButtonsWrapper>
            <PathContinueButtons languagePairId={languagePairId} pathwayNodeId={pathwayNodeId} />
          </S.PathButtonsWrapper>
        ) : (
          <S.FinishButton
            as={LinkButton}
            buttonType="primary"
            buttonSize="large"
            wide
            href={backCta.href}
          >
            {
              /* istanbul ignore next: please test in WEBSITE-7219 */
              experienceType === '2025' ? backCta.text : intl.formatMessage(msgs.finishCta)
            }
          </S.FinishButton>
        )}
        <S.RestartButton buttonType="secondary" buttonSize="large" wide onClick={onRestart}>
          {intl.formatMessage(
            /* istanbul ignore next: please test in WEBSITE-7219 */
            experienceType === '2025' ? msgs.restartCta2025 : msgs.restartCta2024,
          )}
        </S.RestartButton>
      </S.Layout>
    </PronunciationViewWrapper>
  );
};
